import React, { useEffect, useRef, useState, Ref } from 'react';
import '@ui5/webcomponents-icons/dist/person-placeholder';
import '@ui5/webcomponents-icons/dist/nav-back';
import '@ui5/webcomponents-icons/dist/sys-help';
import '@ui5/webcomponents-icons/dist/message-popup';
import '@ui5/webcomponents-icons/dist/feedback';
import '@ui5/webcomponents-icons/dist/learning-assistant';
import '@ui5/webcomponents-icons/dist/bell';
import {
  Avatar,
  Button,
  Icon,
  List,
  ListSeparators,
  Popover,
  PopoverDomRef,
  ShellBar,
  ShellBarItem,
  StandardListItem,
  Text
} from '@ui5/webcomponents-react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWebAssistant } from '../WebAssistant/useWebAssistant';
import {
  shellbarDocumentationInternalConfig,
  shellbarDocumentationLSPConfig,
  shellbarFeedbackConfig,
  shellbarTicketConfig
} from './ShellBarTicketConfig';
import { useGetUserInfoQuery } from 'generated/graphql';

import sapLogo from '../../../sap-logo.svg';
import { UnsupportedBrowserWarning } from 'components/UnsupportedBrowserWarning/UnsupportedBrowserWarning';
import { openWindow } from 'utils/windowOpen';
import { useStyles } from './AppShellBar.jss';
import { ManageSubscriptionsDialog } from './Subscriptions/ManageSubscriptionsDialog';
import { env } from 'config/env';

export const AppShellBar: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { webAssistant } = useWebAssistant();
  const classes = useStyles();

  const isHome = () => location.pathname === '/queries';
  const [{ data: userData }] = useGetUserInfoQuery({ requestPolicy: 'network-only' });
  const [shouldDisplayStartButton, setShouldDisplayStartButton] = useState<boolean>(!isHome());

  const [isSubscriptionsDialogOpen, setIsSubscriptionsDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    setShouldDisplayStartButton(!isHome());
  }, [location]);

  const popoverRef: Ref<PopoverDomRef> = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const goHome = () => {
    if (!isHome()) {
      history.push('/queries');
    }
  };

  const isInternalUser = () => {
    return userData?.user?.internal ?? false;
  };

  const handleTicketItemClick = () => {
    const url = shellbarTicketConfig();
    openWindow(url);
  };

  const handleFeedbackItemClick = () => {
    const url = shellbarFeedbackConfig();
    openWindow(url);
  };

  const handleDocumentationItemClick = () => {
    const url = isInternalUser()
      ? shellbarDocumentationInternalConfig()
      : shellbarDocumentationLSPConfig();
    openWindow(url);
  };

  const handleProfileItemClick = (e: any) => {
    if (popoverRef.current) {
      popoverRef.current.opener = e.target.profileDomRef;
      setPopoverOpen((prev) => !prev);
    }
  };

  const getUserInitials = () => {
    return `${userData?.user?.firstName.at(0)}${userData?.user?.lastName.at(0)}`;
  };

  const renderStartButton = (): JSX.Element | null => {
    return shouldDisplayStartButton ? (
      <Button
        data-help-id="shellbar-button-back"
        icon="nav-back"
        onClick={goHome}
        data-testid="shellbar-start-button"
      />
    ) : null;
  };

  const renderWebAssistant = (): JSX.Element | null => {
    if (webAssistant.isAvailable) {
      return (
        <ShellBarItem
          icon="sys-help"
          onClick={() => webAssistant.internalApi?.toggle()}
          title={t('shellbar-tooltip-web-assistant')}
        />
      );
    }
    return null;
  };

  const getUserSupplierInfo = () => {
    if (isInternalUser()) {
      return 'SAP';
    } else {
      return userData?.user?.suppliers?.map((s) => s.name).join(', ');
    }
  };

  const onSubscriptionsClick = (): void => {
    setIsSubscriptionsDialogOpen(true);
    if (popoverRef.current) {
      popoverRef.current.close();
    }
  };

  const headerText = `${userData?.user?.firstName} ${
    userData?.user?.lastName
  } (${getUserSupplierInfo()})`;

  const renderUserInfoPopover = (): JSX.Element => (
    <Popover
      data-testid="userinfo-popover"
      placementType={'Bottom'}
      ref={popoverRef}
      open={popoverOpen}
      onAfterClose={() => setPopoverOpen(false)}
      headerText={headerText}
    >
      <List separators={ListSeparators.Inner}>
        {env?.REACT_APP_ENABLE_SUBSCRIPTIONS === 'true' && (
          <StandardListItem
            key="subscriptions"
            data-testid="subscriptions"
            onClick={onSubscriptionsClick}
          >
            <div data-help-id="subscriptions-button" className={classes.profileListItem}>
              <Icon name="bell" />
              <Text wrapping={false} className={classes.profileListItem}>
                {t('shellbar-subscriptions')}
              </Text>
            </div>
          </StandardListItem>
        )}
      </List>
    </Popover>
  );

  return (
    <>
      <ShellBar
        startButton={renderStartButton()}
        data-help-id="shellbar-app-title"
        data-testid="shellbar"
        primary-title={t('shellbar-app-title')}
        onLogoClick={goHome}
        logo={<img alt={t('shellbar-sap-logo')} src={sapLogo} />}
        profile={<Avatar data-testid="shellbar-avatar" initials={getUserInitials()}></Avatar>}
        onProfileClick={handleProfileItemClick}
      >
        <ShellBarItem
          data-testid="shellbar-feedback-item"
          icon="feedback"
          onClick={handleFeedbackItemClick}
          title={t('shellbar-tooltip-feedback')}
        />
        <ShellBarItem
          data-testid="shellbar-ticket-item"
          icon="message-popup"
          onClick={handleTicketItemClick}
          title={t('shellbar-tooltip-ticket')}
        />
        <ShellBarItem
          data-testid="shellbar-documentation"
          icon="learning-assistant"
          onClick={handleDocumentationItemClick}
          title={t('shellbar-tooltip-documentation')}
        />
        {renderWebAssistant()}
      </ShellBar>
      <UnsupportedBrowserWarning></UnsupportedBrowserWarning>
      {renderUserInfoPopover()}
      <ManageSubscriptionsDialog
        isOpen={isSubscriptionsDialogOpen}
        setIsSubscriptionsDialogOpen={setIsSubscriptionsDialogOpen}
      />
    </>
  );
};
